/** What a run has staged: its state-document and prompt changes. */
import { type ReactNode, useState } from "react";

import type {
  CompletionProposal,
  InstructionChangeProposal,
  StateChangeProposal,
} from "../api/hooks/useStatefulAgents";
import type { AgentStateDiffLine } from "../api/models/agentState";
import { RULE_ACTION } from "../utils/ruleActions";
import { DiffView } from "./DiffView";
import styles from "./RunView.module.css";
import { Chevron } from "./Chevron";



// One agent's proposed state document, folded: documents run to hundreds of lines
// and would bury the decision row. The heading says whose and how much changed.
const StateChangeCard = ({ change }: { change: StateChangeProposal }) => {
  const [open, setOpen] = useState(false);
  const added = change.diff.filter((l: AgentStateDiffLine) => l.kind === "added").length;
  const removed = change.diff.filter((l: AgentStateDiffLine) => l.kind === "removed").length;
  return (
    <div className={styles.change}>
      <button
        type="button"
        className={styles.changeHeader}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className={styles.changeCaret} aria-hidden="true">
          <Chevron tree open={open} />
        </span>
        <span className={styles.changeTitle}>{change.label} - state document</span>
        <span className={styles.muted}>
          {change.base_version ? `updating v${change.base_version}` : "first version"}
        </span>
        <span className={styles.changeCounts}>
          {added > 0 && <span className={styles.countAdded}>+{added}</span>}
          {removed > 0 && <span className={styles.countRemoved}>-{removed}</span>}
        </span>
      </button>
      {change.row_fields_changed.length > 0 && (
        <div className={styles.muted}>
          portfolio row: {change.row_fields_changed.join(", ")}
        </div>
      )}
      {open && <DiffView lines={change.diff} />}
    </div>
  );
};

// One proposed rule change, folded to its heading. The tick sits OUTSIDE the fold
// button (a checkbox inside a <button> can't be operated), as do the warnings,
// which change what accepting does.
const InstructionChangeCard = ({
  change,
  consolidated,
  ticked,
  setTicked,
}: {
  change: InstructionChangeProposal;
  consolidated: boolean;
  ticked: boolean;
  setTicked: (ticked: boolean) => void;
}) => {
  const [open, setOpen] = useState(false);
  const added = change.diff.filter((l: AgentStateDiffLine) => l.kind === "added").length;
  const removed = change.diff.filter((l: AgentStateDiffLine) => l.kind === "removed").length;
  // `retire R9 (Chase the DNO weekly)`, or just `a new rule`.
  const subject = [change.rule_id, change.rule_title ? `(${change.rule_title})` : ""]
    .filter(Boolean)
    .join(" ");
  const title =
    `${change.label} - ${RULE_ACTION[change.action] ?? change.action}` +
    (subject ? ` ${subject}` : "");
  return (
    <div className={styles.change}>
      <div className={styles.instructionHead}>
        {consolidated && (
          <input
            type="checkbox"
            checked={ticked}
            disabled={!!change.unavailable_reason}
            aria-label={`Accept ${title}`}
            onChange={(e) => setTicked(e.target.checked)}
          />
        )}
        <button
          type="button"
          className={styles.changeHeader}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span className={styles.changeCaret} aria-hidden="true">
            <Chevron tree open={open} />
          </span>
          <span className={styles.changeTitle}>{title}</span>
          <span className={styles.changeCounts}>
            {added > 0 && <span className={styles.countAdded}>+{added}</span>}
            {removed > 0 && <span className={styles.countRemoved}>-{removed}</span>}
          </span>
        </button>
      </div>
      {change.unavailable_reason && (
        <p className={styles.error}>Cannot be applied: {change.unavailable_reason}</p>
      )}
      {change.rebased_onto_revision !== null && !change.unavailable_reason && (
        <p className={styles.muted}>
          Proposed against r{change.base_revision}; shown against the current r
          {change.rebased_onto_revision}, which is what accepting will change.
        </p>
      )}
      {open && (
        <>
          <p className={styles.rationale}>{change.rationale}</p>
          <DiffView lines={change.diff} />
        </>
      )}
    </div>
  );
};

const countLabel = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? "" : "s"}`;

// "Grid (2), Manager", so a fully-folded section still says what is in it.
const summariseByLabel = (labels: string[]): string => {
  const counts = new Map<string, number>();
  for (const label of labels) counts.set(label, (counts.get(label) ?? 0) + 1);
  return [...counts.entries()].map(([label, n]) => (n > 1 ? `${label} (${n})` : label)).join(", ");
};

// Separate sections: a state document is THIS project's record, an instruction
// change rewrites the playbook for EVERY project.
const ChangeGroup = ({
  title,
  note,
  summary,
  children,
}: {
  title: string;
  note: string | null;
  summary: string;
  children: ReactNode;
}) => (
  <section className={styles.changeSection}>
    <h4 className={styles.changeGroup}>
      {title}
      {note && <span className={styles.muted}> - {note}</span>}
    </h4>
    <p className={styles.groupSummary}>{summary}</p>
    {children}
  </section>
);

/** Per-rule accept ticks, or ``null`` on a surface with nothing to decide. */
interface Ticking {
  isTicked: (change: InstructionChangeProposal) => boolean;
  setTicked: (proposalId: string, ticked: boolean) => void;
}

/** What a run has staged - the two change sections, and no decision row.
 *
 * Shared by the scheduled-run and conversation pages so the surfaces can't
 * drift. Read-only on both: nothing here waits for an accept step - a pass's
 * staged rule changes are settled by replying, and a conversation's changes land
 * as it makes them.
 */
export const StagedChanges = ({
  proposal,
  ticking,
}: {
  proposal: CompletionProposal;
  ticking: Ticking | null;
}) => (
  <>
    {proposal.state_changes.length > 0 && (
      <ChangeGroup
        title="Project specific state document changes"
        note={null}
        summary={`${countLabel(
          proposal.state_changes.length,
          "state document",
        )} - ${summariseByLabel(proposal.state_changes.map((c) => c.label))}`}
      >
        {proposal.state_changes.map((change) => (
          <StateChangeCard key={`s-${change.agent}`} change={change} />
        ))}
      </ChangeGroup>
    )}
    {proposal.instruction_changes.length > 0 && (
      <ChangeGroup
        title="Global agent prompt changes"
        note="these apply to every project this agent works on"
        summary={`${countLabel(
          proposal.instruction_changes.length,
          "rule change",
        )} - ${summariseByLabel(proposal.instruction_changes.map((c) => c.label))}`}
      >
        {proposal.instruction_changes.map((change) => (
          <InstructionChangeCard
            key={change.proposal_id}
            change={change}
            consolidated={ticking !== null}
            ticked={ticking !== null && ticking.isTicked(change)}
            setTicked={(t) => ticking?.setTicked(change.proposal_id, t)}
          />
        ))}
      </ChangeGroup>
    )}
  </>
);
